import { useReactionController } from "@rosette/web_components/src/game/reactions/ReactionUIContext.js";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactionDirective } from "@rosette/ts/src/business/game/controller/reactions/ReactionDirective.js";
import { ReactionIcon } from "@/components/icon/ReactionIcon.js";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@rosette/ts/src/util/cn.js";
import { useAssetLoader } from "@/components/assets/AssetContext.js";
import { ReactionResources } from "@/components/game/reactions/ReactionResources.js";
import { useGameDirective, useGameSource } from "@/components/game/GameUIContext.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { useIsEpochTimeInPast } from "@rosette/web_utils/src/time/useIsEpochTimeInPast.js";
import { SPINNER_DURATION_MS, TimingSpinner } from "@rosette/web_components/src/game/timing/TimingSpinner.js";
import { fadeInOut } from "@/utils/fadeInOut.js";


export function useReactionResources(): ReactionResources {
    const assetLoader = useAssetLoader();
    return useMemo(() => {
        return new ReactionResources(assetLoader);
    }, [assetLoader]);
}


interface PlayedReactionProps {
    reactionDirective: ReactionDirective;
    positionLeft: boolean;
    onFinish: (directive: ReactionDirective) => void;
}

function playedReactionContainerClassName(positionLeft: boolean): string {
    return cn(
        "absolute p-3 -top-[2.5em] md:-top-[2em]",
        positionLeft
            ? "left-[min(20vw,max(calc(100%_-_2.5em),1em))] md:left-[max(calc(100%_-_2em),1em)]"
            : "right-[min(20vw,max(calc(100%_-_2.5em),1em))] md:right-[max(calc(100%_-_2em),1em)]",
    );
}


function PlayedReaction({ reactionDirective, positionLeft, onFinish }: PlayedReactionProps) {
    if (!reactionDirective.reaction)
        throw new Error("Directives without a reaction shouldn't be rendered");

    const onComplete = useCallback(() => {
        onFinish(reactionDirective);
    }, [reactionDirective, onFinish]);

    return (
        <motion.div
            key={reactionDirective.getDirectiveID()}
            className={playedReactionContainerClassName(positionLeft)}
            initial={{
                opacity: 0,
                scale: 0.5,
            }}
            animate={{
                opacity: 1,
                scale: 1.0,
            }}
            exit={{
                opacity: 0,
                scale: 0.5,
            }}
            transition={{
                ease: "easeInOut" as const,
                duration: 0.2,
            }}>

            <ReactionIcon
                className={cn(
                    "w-[4em] h-[4em] max-w-[4em] max-h-[4em] md:w-[3em] md:h-[3em] md:max-w-[3em] md:max-h-[3em]",
                    positionLeft ? "translate-x-1/2 translate-y-1/2" : "-translate-x-1/2 translate-y-1/2",
                )}
                reaction={reactionDirective.reaction}
                onComplete={onComplete}
                autoPlay={true} />
        </motion.div>
    );
}


interface ReactionPlaybackProps {
    className?: string;
    player: PlayerType | null;
    yourPlayer: PlayerType | null;
    positionLeft: boolean;
    disabled: boolean;
}

export function ReactionPlayback({
    className, player, yourPlayer, positionLeft, disabled,
}: ReactionPlaybackProps) {

    // We show the timer spinner instead of reactions when a timer is close.
    const gameSource = useGameSource();
    const gameDirective = useGameDirective();
    const timeControlState = useRune(gameSource.timeControlState);
    const firstTimerMs = timeControlState.getFirstTimeMs();
    const timeSpinnerColour = (
        firstTimerMs === timeControlState.gameAbandonTimeMs ? "red" : "blue"
    );
    const timerEndMs = (firstTimerMs !== 0 ? firstTimerMs - 500 : 0);
    const showSpinnerTimeReached = useIsEpochTimeInPast(timerEndMs - SPINNER_DURATION_MS);
    const showSpinner = (
        !gameDirective.state.isFinished() && gameDirective.activePlayer === player
        && timerEndMs > 0 && showSpinnerTimeReached
    );

    const controller = useReactionController();
    const [current, setCurrent] = useState<ReactionDirective[]>([]);
    const resources = useReactionResources();

    useEffect(() => {
        if (player !== yourPlayer)
            return;

        controller.getPlayingOwnPlayerReaction().set(
            current.length !== 0 || showSpinner,
        );
    }, [player, yourPlayer, controller, current, showSpinner]);

    const filterDirective = useCallback((directive: ReactionDirective) => {
        return directive.reaction && directive.player === player;
    }, [player]);

    const processDirective = useCallback((directive: ReactionDirective) => {
        if (!filterDirective(directive))
            return false;

        setCurrent((prev) => {
            if (prev.includes(directive))
                return prev;
            if (disabled)
                return [];

            resources.getReactionNotificationSound().play(true);
            return [...prev, directive];
        });
        return true;
    }, [resources, filterDirective, setCurrent, disabled]);

    const finishDirective = useCallback((directive: ReactionDirective) => {
        setCurrent(prev => prev.filter(d => d !== directive));
    }, [setCurrent]);

    useEffect(() => {
        const activeDirective = controller.getActiveDirective();
        if (processDirective(activeDirective)) {
            controller.popActiveDirective(activeDirective);
        }

        const timeoutIds = new Set<NodeJS.Timeout>();

        const unsubscribe = controller.subscribeToActiveDirective((directive) => {
            const timeoutId = setTimeout(() => {
                if (processDirective(directive)) {
                    controller.popActiveDirective(directive);
                }
                timeoutIds.delete(timeoutId);
            });
            timeoutIds.add(timeoutId);
        });

        return () => {
            unsubscribe();
            timeoutIds.forEach(id => clearTimeout(id));
        };
    }, [controller, processDirective]);

    if (disabled && !showSpinner)
        return (<></>);

    const reactionElems = [];
    if (!showSpinner) {
        for (const reactionDirective of current) {
            if (!reactionDirective.reaction)
                throw new Error("Directives without a reaction shouldn't get added to current");

            reactionElems.push(
                <PlayedReaction
                    key={reactionDirective.getDirectiveID()}
                    reactionDirective={reactionDirective}
                    positionLeft={positionLeft}
                    onFinish={finishDirective} />,
            );
        }
    }

    return (
        <div className={className}>
            <AnimatePresence>
                {reactionElems}

                {showSpinner && (
                    <motion.div
                        key="timer"
                        className={playedReactionContainerClassName(positionLeft)}
                        {...fadeInOut({ delay: 0.25 })}>

                        <TimingSpinner
                            className={positionLeft ? "translate-[2em]" : "-translate-x-[2em] translate-y-[2em]"}
                            endTimeMs={timerEndMs}
                            colour={timeSpinnerColour} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
